import { createRng } from '@battle/sim';
import { color, mix, positionLocal, sin, time, uv, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';

export interface GrassOptions {
  count: number;
  /** Returns true if a blade may grow at (x, z). */
  accept: (x: number, z: number) => boolean;
  /** Terrain height at (x, z). */
  height: (x: number, z: number) => number;
  /** Sampling bounds (square, centered on the origin). */
  extent: number;
  seed?: number;
}

/**
 * Instanced grass blades with a TSL wind sway. Three applies the instance transform before
 * positionNode, so positionLocal is already the blade's world-space position here.
 * The GPU-compute grass with creature and attack interaction comes in M4 (docs/06).
 */
export function createGrass(opts: GrassOptions): THREE.InstancedMesh {
  const rng = createRng(opts.seed ?? 7);

  // One blade: a tapered 4-segment strip with a slight forward curve. Normals point up, which
  // gives the soft, uniformly lit look of stylized grass.
  const segments = 4;
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const w = 0.055 * (1 - t) ** 0.9;
    const bend = 0.12 * t * t;
    positions.push(-w, t, bend, w, t, bend);
    uvs.push(0, t, 1, t);
    if (i < segments) {
      const a = i * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
  }
  // Pointed tip.
  positions.push(0, 1.04, 0.13);
  uvs.push(0.5, 1);
  const tip = (segments + 1) * 2;
  indices.push(tip - 2, tip - 1, tip);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  const normals = new Float32Array((positions.length / 3) * 3);
  for (let i = 1; i < normals.length; i += 3) normals[i] = 1;
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setIndex(indices);

  const material = new THREE.MeshStandardNodeMaterial({ side: THREE.DoubleSide, roughness: 0.8 });
  const h = uv().y;
  material.colorNode = mix(color('#2f4a1c'), color('#d9c46a'), h.pow(1.3));
  const gust = sin(time.mul(0.6).add(positionLocal.x.mul(0.05)))
    .mul(0.5)
    .add(0.5);
  const phase = positionLocal.x.mul(0.45).add(positionLocal.z.mul(0.3));
  const sway = sin(time.mul(1.8).add(phase)).mul(0.08).add(gust.mul(0.12));
  material.positionNode = positionLocal.add(vec3(sway, 0, sway.mul(0.6)).mul(h.mul(h)));

  const mesh = new THREE.InstancedMesh(geometry, material, opts.count);
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const s = new THREE.Vector3();
  const p = new THREE.Vector3();
  const tint = new THREE.Color();
  let placed = 0;
  let attempts = 0;
  while (placed < opts.count && attempts < opts.count * 20) {
    attempts++;
    const x = (rng.next() * 2 - 1) * opts.extent;
    const z = (rng.next() * 2 - 1) * opts.extent;
    if (!opts.accept(x, z)) continue;
    e.set((rng.next() - 0.5) * 0.3, rng.next() * Math.PI * 2, (rng.next() - 0.5) * 0.3);
    q.setFromEuler(e);
    const height = 0.45 + rng.next() * 0.7;
    s.set(1, height, 1);
    p.set(x, opts.height(x, z) - 0.02, z);
    m.compose(p, q, s);
    mesh.setMatrixAt(placed, m);
    // Instance color multiplies the material color, so keep tints near white.
    tint.setHSL(0.12 + rng.next() * 0.1, 0.25 + rng.next() * 0.2, 0.72 + rng.next() * 0.22);
    mesh.setColorAt(placed, tint);
    placed++;
  }
  mesh.count = placed;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  return mesh;
}
